export const meta = {
  id: '06-demonstratives-1',
  order: 6,
  titleFi: 'Osoittavat pronominit 1',
  titleEn: 'Demonstratives 1',
  duolingoUnit: 'duo-04-demonstratives-1',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['kesy', 'bisy', 'kony', 'bony', 'kesir', 'arlī', 'vestragon', 'skorkydoso', 'ours:jehikvos', 'ours:hannavos', 'ours:barduvos', 'ours:iosrālion', 'ours:bēmvos', 'ours:bāngy', 'ours:tembyrītsos', 'ours:kervos', 'ours:iārvos', 'ours:qrīdrūvos', 'ours:rhakiapos', 'ours:riñapos', 'ours:bēmītsos', 'ours:pryjatir', 'ours:fotografia', 'ours:komputeri', 'ours:urnēbvos', 'ours:batteri', 'ours:laturi', 'ours:vakūmi', 'ours:jehikiapos', 'ours:kamera', 'ours:ryptevos', 'ours:sētāzma', 'ours:folderi', 'ours:grevenkiapos', 'ours:motori', 'ours:roboti', 'ours:klavieri', 'ours:aktovka'],
  exampleIds: [
    'ex-11abb15c4a9a',
    'ex-83c7acca6be6',
    'ex-2de825d91902',
    'ex-702111db756e',
    'got-s7e07-005',
    'ex-7492650d0c51',
    'ex-6c81f0e08b6f',
    'got-s3e08-002',
    'ex-e89b8e3ea14e',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Demonstratives_1',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns#Demonstrative_and_interrogative_pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Personal_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=Kesy',
    'https://wiki.languageinvention.com/index.php?title=Bisy',
    'https://wiki.languageinvention.com/index.php?title=Bisa',
    'https://wiki.languageinvention.com/index.php?title=Kesa',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['demonstratives'],
  showLines: ['got-s7e07-005', 'got-s3e08-002'],
  pronunciation: { focus: 'z', words: ['azantys', 'sȳz'], lines: ['got-s7e07-005', 'got-s3e08-002'] },
  letter: {
    fi: 'Kuvaile kolmea asiaa ympärilläsi: tämä, tuo.',
    en: 'Describe three things around you: this, that.',
  },
  // an everyday scene of ours (approved 2026-10-07, together/everyday/): the week has no show scene of its own
  scene: 'ours-tervehdys',
}

# Osoittavat pronominit 1

Tällä oppitunnilla opitaan sanomaan *tämä* ja *tuo*. Sanoja on neljä, ja ne jakautuvat kahden kysymyksen mukaan: onko asia lähellä vai kauempana, ja onko se elollinen vai eloton.

## Neljä sanaa, kaksi rajaa

- <Hv glossed>kesy</Hv> – tämä (eloton)
- <Hv glossed>bisy</Hv> – tämä (elollinen)
- <Hv glossed>kony</Hv> – tuo (eloton)
- <Hv glossed>bony</Hv> – tuo (elollinen)

<Pronounce word="kesy" />

<Pronounce word="bisy" />

Alkukirjain kertoo elollisuuden. Duolingon Tips & Notes -ohjeissa elolliset ovat **B-luokkaa** ja elottomat **K-luokkaa**: *b*-alkuinen sana viittaa ihmiseen tai muuhun elolliseen, *k*-alkuinen esineeseen tai asiaan. Keskimmäinen vokaali kertoo etäisyyden: *-e-* ja *-i-* tarkoittavat lähellä olevaa (’tämä’), *-o-* kauempana olevaa (’tuo’).

<Example id="ex-11abb15c4a9a" />

<Example id="ex-83c7acca6be6" />

<Example id="ex-2de825d91902" />

<Example id="ex-702111db756e" />

<Interlinear hv="Kes|y ñuh|ys klios issa." gloss="tämä-NOM minun-SOL.NOM kala-NOM olla-PRS.3SG" fi="Tämä on minun kalani." />

## Mikä tästä on suomalaiselle tuttua

Etäisyysraja on suomalaiselle tuttu, mutta suomessa portaita on kolme: *tämä*, *tuo* ja *se*. Korkeavalyriassa niitä on kaksi, joten *tämä* ja *tuo* riittävät.

Elollisuusraja on tutumpi kuin ensin ehkä tuntuu. Suomen kirjakielessä erotamme ihmisen ja asian kolmannessa persoonassa: *hän* ihmisestä, *se* asiasta. Korkeavalyriassa sama raja kulkee näissä osoittavissa pronomineissa. Kolmannen persoonan <Hv>ziry</Hv> ja <Hv>ūja</Hv> valitaan kuitenkin suvun mukaan (<LessonLink n={18}>oppitunti 18</LessonLink>): wikin kielioppisivu kuvaa jaon elollisuutena, Duolingon ohjeet ja Peterson suvun mukaan. Suomen *tämä* ja *tuo* eivät erottele ihmistä ja esinettä, joten tämä raja on opeteltava uutena.

<Line id="got-s7e07-005" />

Daenerysin repliikissä näkyvät molemmat puolet: <Hv glossed>kesy</Hv> ’tämä’ viittaa sotaan (eloton), mutta ’tämä mies’ alkaa *b*:llä, koska mies on elollinen.

## Eläimet: puhuja päättää

Duolingon ohjeiden mukaan puhuja saa itse päättää, ovatko eläimet tarpeeksi elollisia B-luokan pronominille. Wikin pronominisivun mukaan ratkaisu riippuu eläimestä ja siitä, miten puhuja siihen suhtautuu. Suomessa sama joustavuus on tuttu: lemmikkikoirasta voi joskus sanoa *hän*, vaikka kirjakielen sääntö vaatisi *se*.

Wikin esimerkeissä samasta eläimestä käytetään kumpaakin. Näissä lauseissa ’tuo’ on substantiivin edellä adjektiivina, mihin palataan alempana, mutta elollisuusraja toimii samoin:

<Example id="ex-7492650d0c51" />

<Example id="ex-6c81f0e08b6f" />

Ensimmäisessä lauseessa lintu on *k*-sanan mukaan eloton, toisessa *b*-sanan mukaan elollinen. Molemmat ovat oikein. Samoin oppitunnin alun kalaesimerkissä kala on <Hv>kesy</Hv> eli eloton.

## Ne eivät mukaudu substantiiviin

Tämä on Duolingon ohjeiden tärkein varoitus. Adjektiivit mukautuvat substantiiviinsa sijassa, luvussa ja suvussa. Nämä osoittavat pronominit eivät mukaudu: niillä on oma lukunsa, joka riippuu siitä, mihin ne viittaavat. Kun puhut yhdestä asiasta, sanot <Hv>kesy</Hv>. Kun puhut monesta, sanot <Hv l="kesy">kesi</Hv>.

Pronomini kuitenkin taipuu itse lauseessa. Sillä on oma nominatiivinsa ja akkusatiivinsa niin kuin substantiivilla:

<Paradigm lemma="kesy" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="bisy" cases="nom,acc" numbers="sg,pl" />

Huomaa, että <Hv glossed>kesi</Hv> on sekä monikon nominatiivi ’nämä’ että yksikön akkusatiivi ’tämän’. Lauseen verbi ja asiayhteys kertovat, kumpi on kyseessä. Monikon akkusatiivissa vokaali on pitkä: <Seam s="kes" e="ī" />. Pituus kantaa jälleen kieliopillista tietoa.

<Compare l="kesy" fi="tämä|n" hv="kes|i" label="objekti yksikössä" />

<Compare fi="nämä" hv="kes|ī" label="objekti monikossa" />

## Tämä mies, tämä kypärä

Kun ’tämä’ on substantiivin edellä, wikin sanakirjassa käytetään sen sijaan omaa adjektiivia. Pronominit on itse asiassa johdettu näistä adjektiiveista: wikin mukaan <Hv>bisy</Hv> on muodostettu adjektiivista <Hv>bisa</Hv>.

- <Hv glossed>kesa</Hv> – tämä (eloton), adjektiivina
- <Hv glossed>bisa</Hv> – tämä (elollinen), adjektiivina
- <Hv glossed>kona</Hv> – tuo (eloton), adjektiivina
- <Hv glossed>bona</Hv> – tuo (elollinen), adjektiivina

Toisin kuin pronominit, nämä taipuvat kuin adjektiivit ja mukautuvat substantiiviinsa. Wikin pronominisivun esimerkeissä on <Hv glossed>bisa muña</Hv> ’tämä äiti’ mutta <Hv glossed>bisys zaldrīzes</Hv> ’tämä lohikäärme’, koska äiti ja lohikäärme kuuluvat eri sukuihin. Wikin mukaan osoittava sana tulee lähes aina substantiivin eteen.

<Line id="got-s3e08-002" />

<Example id="ex-e89b8e3ea14e" />

’Nämä miehet’ on <Hv glossed>bisi vali</Hv>: adjektiivi on monikossa, koska miehiä on monta. ’Tämä kypärä’ on <Hv glossed>kesa gelte</Hv>. Kypärä on eloton, joten sana alkaa *k*:lla.

Duolingon ohjeet käsittelevät tässä osiossa vain itsenäisiä pronomineja. Riittää, että tunnistat adjektiivit, kun ne tulevat vastaan.

<Note>
Wikin pronominisivulla itsenäisille pronomineille on myös muodot <Hv l="kesir">kesir</Hv>, <Hv>bisir</Hv>, <Hv l="konir">konir</Hv> ja <Hv>bonir</Hv>. Niillä puhutaan abstrakteista tai ei-laskettavista asioista: <Hv glossed l="kesir">kesir</Hv> ja <Hv glossed l="konir">konir</Hv> elottomista asioista ja paikoista, <Hv glossed>bisir</Hv> ja <Hv glossed>bonir</Hv> elollisista. *-y*-loppuisilla puhutaan konkreettisista, laskettavista asioista. Duolingo käyttää *-y*-loppuisia muotoja.
</Note>

## Apufraasit

Sanalla <Hv l="kesir">kesir</Hv> syntyy kaksi apufraasia lisää: ’se’ ja ’tämä’ viittaavat niissä sanottuun asiaan, joka on abstrakti. Opettele nekin kokonaisina.

<Practice hv="Kesir arlī vestrās." fi="Sano se uudestaan." />

<Practice hv="Skorkydoso kesir vestri?" fi="Miten tämä sanotaan?" />

Ensimmäinen on muodostettu Petersonin lauseen <Hv>Yne arlī qrīdrughās sepār morghūljās.</Hv> mallilla, toinen lauseen <Hv>Skoros morghot vestri?</Hv> mallilla. Kun et tiedä, miten jokin sanotaan, sano se ensin suomeksi ja kysy sitten.

## Harjoitellaan

<Practice hv="Bisy ābra issa." fi="Tämä on nainen." />

<Practice hv="Kony korze issa." fi="Tuo on pitkämiekka." />

<Practice hv="Bony azantys issa." fi="Tuo on ritari." />

<Practice hv="Bisi urnen." fi="Näen tämän (ihmisen)." />

<Practice hv="Konī urnen." fi="Näen nuo (esineet)." />

Kahdessa viimeisessä lauseessa pronomini on objektina, joten se on akkusatiivissa.

<Vocab />

<Check />
